import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, signIn, signUp, signOut, signInWithGoogle, getSession, db } from '../lib/supabase';

// Subscription tier limits
const TIER_LIMITS = {
  free: { deals: 3, mediaKits: 1 },
  starter: { deals: 10, mediaKits: 3 },
  pro: { deals: 30, mediaKits: 10 },
  business: { deals: 100, mediaKits: -1 }, // -1 = unlimited
  enterprise: { deals: -1, mediaKits: -1 },
};

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current tier limits
  const getTierLimits = useCallback(() => {
    const tier = subscription?.tier || 'free';
    return TIER_LIMITS[tier] || TIER_LIMITS.free;
  }, [subscription]);

  // Check if user can create more deals
  const canCreateDeal = useCallback(async () => {
    if (!user) return false;
    const limits = getTierLimits();
    if (limits.deals === -1) return true; // unlimited
    
    const { count } = await db.getActiveDealsCount(user.id);
    return (count || 0) < limits.deals;
  }, [user, getTierLimits]);

  // Load user profile and subscription
  const loadUserData = useCallback(async (userId) => {
    try {
      // Try to load profile and subscription, but don't fail if tables don't exist
      const [profileResult, subscriptionResult] = await Promise.allSettled([
        db.getProfile(userId),
        db.getSubscription(userId),
      ]);

      if (profileResult.status === 'fulfilled' && profileResult.value.data) {
        setProfile(profileResult.value.data);
      }

      if (subscriptionResult.status === 'fulfilled' && subscriptionResult.value.data) {
        setSubscription(subscriptionResult.value.data);
      } else {
        // Default to free tier if no subscription or error
        setSubscription({ tier: 'free', status: 'active' });
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      // Default to free tier on any error
      setSubscription({ tier: 'free', status: 'active' });
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // Reduce timeout to 3 seconds for faster loading
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ session: null }), 3000)
        );
        
        const sessionPromise = getSession();
        
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        const session = result?.session;
        
        if (mounted) {
          if (session?.user) {
            setUser(session.user);
            // Don't await this - let it load in background
            loadUserData(session.user.id).catch(console.error);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          setLoading(false);
          // Don't set error - just show login page
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (mounted) {
          if (event === 'SIGNED_IN' && session?.user) {
            setUser(session.user);
            await loadUserData(session.user.id);
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setProfile(null);
            setSubscription(null);
          } else if (event === 'USER_UPDATED' && session?.user) {
            setUser(session.user);
          }
        }
      }
    );

    return () => {
      mounted = false;
      authSubscription.unsubscribe();
    };
  }, [loadUserData]);

  // Auth methods
  const login = useCallback(async (email, password) => {
    setError(null);
    const { data, error: loginError } = await signIn(email, password);
    if (loginError) {
      setError(loginError.message);
      return { error: loginError };
    }
    return { data };
  }, []);

  const register = useCallback(async (email, password, metadata = {}) => {
    setError(null);
    const { data, error: registerError } = await signUp(email, password, metadata);
    if (registerError) {
      setError(registerError.message);
      return { error: registerError };
    }
    return { data };
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    const { data, error: googleError } = await signInWithGoogle();
    if (googleError) {
      setError(googleError.message);
      return { error: googleError };
    }
    return { data };
  }, []);

  const logout = useCallback(async () => {
    const { error: logoutError } = await signOut();
    if (logoutError) {
      setError(logoutError.message);
      return { error: logoutError };
    }
    return { success: true };
  }, []);

  const updateUserProfile = useCallback(async (updates) => {
    if (!user) return { error: new Error('Not authenticated') };
    
    const { data, error: updateError } = await db.updateProfile(user.id, updates);
    if (updateError) {
      setError(updateError.message);
      return { error: updateError };
    }
    
    setProfile(data);
    return { data };
  }, [user]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    // State
    user,
    profile,
    subscription,
    loading,
    error,
    isAuthenticated: !!user,
    tier: subscription?.tier || 'free',
    
    // Methods
    login,
    register,
    loginWithGoogle,
    logout,
    updateUserProfile,
    clearError,
    
    // Subscription helpers
    getTierLimits,
    canCreateDeal,
    
    // Tier checks
    isPro: ['pro', 'business', 'enterprise'].includes(subscription?.tier),
    isBusiness: ['business', 'enterprise'].includes(subscription?.tier),
    isEnterprise: subscription?.tier === 'enterprise',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
